import { useRef, useState, useCallback, useEffect } from "react";

interface SharePanelProps {
  numbers: number[];
  isComplete: boolean;
}

const BALL_COLORS_HEX = [
  { fill: "#fbbf24", stroke: "#b45309" },
  { fill: "#f87171", stroke: "#b91c1c" },
  { fill: "#34d399", stroke: "#047857" },
  { fill: "#60a5fa", stroke: "#1d4ed8" },
  { fill: "#c084fc", stroke: "#7c3aed" },
  { fill: "#fb923c", stroke: "#c2410c" },
];

function drawResultCard(canvas: HTMLCanvasElement, numbers: number[]) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const W = 600;
  const H = 380;
  canvas.width = W;
  canvas.height = H;

  // Background
  const bgGrad = ctx.createLinearGradient(0, 0, W, H);
  bgGrad.addColorStop(0, "#1a1a2e");
  bgGrad.addColorStop(0.5, "#16213e");
  bgGrad.addColorStop(1, "#1a1a2e");
  ctx.fillStyle = bgGrad;
  ctx.beginPath();
  ctx.roundRect(0, 0, W, H, 20);
  ctx.fill();

  // Border
  ctx.strokeStyle = "#f59e0b";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.roundRect(2, 2, W - 4, H - 4, 18);
  ctx.stroke();

  // Inner glow
  const glowGrad = ctx.createRadialGradient(
    W / 2,
    H * 0.3,
    30,
    W / 2,
    H * 0.3,
    300
  );
  glowGrad.addColorStop(0, "rgba(251, 191, 36, 0.08)");
  glowGrad.addColorStop(1, "rgba(251, 191, 36, 0)");
  ctx.fillStyle = glowGrad;
  ctx.fillRect(0, 0, W, H);

  // Trophy icon (simple circle)
  ctx.beginPath();
  ctx.arc(W / 2, 55, 28, 0, Math.PI * 2);
  const trophyGrad = ctx.createRadialGradient(
    W / 2 - 5,
    50,
    3,
    W / 2,
    55,
    28
  );
  trophyGrad.addColorStop(0, "#fcd34d");
  trophyGrad.addColorStop(1, "#d97706");
  ctx.fillStyle = trophyGrad;
  ctx.fill();
  ctx.font = "bold 28px sans-serif";
  ctx.fillStyle = "#fff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("\uD83C\uDFC6", W / 2, 56);

  // Title
  ctx.font = "bold 22px Orbitron, sans-serif";
  ctx.fillStyle = "#fbbf24";
  ctx.textAlign = "center";
  ctx.fillText("PINBALL", W / 2 - 38, 105);
  ctx.fillStyle = "#f97316";
  ctx.fillText("SIX", W / 2 + 52, 105);

  // Subtitle
  ctx.font = "14px sans-serif";
  ctx.fillStyle = "rgba(251, 191, 36, 0.7)";
  ctx.fillText(
    "\uD540\uBCFC\uB85C \uBF51\uC740 \uC624\uB298\uC758 \uD589\uC6B4 \uBC88\uD638",
    W / 2,
    130
  );

  // Balls
  const sorted = [...numbers].sort((a, b) => a - b);
  const ballR = 34;
  const totalBallsW = sorted.length * (ballR * 2 + 14) - 14;
  const startX = (W - totalBallsW) / 2 + ballR;
  const ballY = 190;

  sorted.forEach((num, i) => {
    const cx = startX + i * (ballR * 2 + 14);
    const col = BALL_COLORS_HEX[i];

    // Shadow
    ctx.beginPath();
    ctx.arc(cx + 3, ballY + 3, ballR, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(0,0,0,0.3)";
    ctx.fill();

    // Glow
    ctx.beginPath();
    ctx.arc(cx, ballY, ballR + 6, 0, Math.PI * 2);
    ctx.fillStyle = col.fill + "30";
    ctx.fill();

    // Ball - reduced reflection (matte look)
    ctx.beginPath();
    ctx.arc(cx, ballY, ballR, 0, Math.PI * 2);
    const bGrad = ctx.createRadialGradient(
      cx - 6,
      ballY - 6,
      2,
      cx + 4,
      ballY + 4,
      ballR
    );
    bGrad.addColorStop(0, col.fill);
    bGrad.addColorStop(0.7, col.fill);
    bGrad.addColorStop(1, col.stroke);
    ctx.fillStyle = bGrad;
    ctx.fill();
    ctx.strokeStyle = col.stroke;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Subtle highlight (smaller, less bright)
    ctx.beginPath();
    ctx.arc(cx - 8, ballY - 8, 5, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.18)";
    ctx.fill();

    // Number - bigger and bolder
    ctx.font = "bold 26px Orbitron, sans-serif";
    ctx.fillStyle = "#fff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.shadowColor = "rgba(0,0,0,0.6)";
    ctx.shadowBlur = 5;
    ctx.fillText(String(num), cx, ballY + 1);
    ctx.shadowBlur = 0;
  });

  // Divider
  ctx.strokeStyle = "rgba(251, 191, 36, 0.2)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(80, 250);
  ctx.lineTo(W - 80, 250);
  ctx.stroke();

  // Date
  const now = new Date();
  const dateStr = `${now.getFullYear()}.${String(
    now.getMonth() + 1
  ).padStart(2, "0")}.${String(now.getDate()).padStart(2, "0")}`;
  ctx.font = "13px sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.4)";
  ctx.fillText(dateStr, W / 2, 278);

  // Footer - "로또 구매는 신중히, 국밥은 과감히" bigger and yellow
  ctx.font = "bold 18px sans-serif";
  ctx.fillStyle = "#fbbf24";
  ctx.fillText(
    "\uB85C\uB610 \uAD6C\uB9E4\uB294 \uC2E0\uC911\uD788, \uAD6D\uBC25\uC740 \uACFC\uAC10\uD788. \uD83C\uDF72",
    W / 2,
    318
  );

  ctx.font = "11px sans-serif";
  ctx.fillStyle = "rgba(251, 191, 36, 0.5)";
  ctx.fillText(
    "PINBALL SIX \u00B7 pinball-lotto.readdy.ai",
    W / 2,
    355
  );
}

export default function SharePanel({
  numbers,
  isComplete,
}: SharePanelProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [showToast, setShowToast] = useState("");

  /* -------------------------------------------------
   * Draw the result card when the component becomes ready
   * ------------------------------------------------- */
  useEffect(() => {
    if (isComplete && canvasRef.current && numbers.length === 6) {
      drawResultCard(canvasRef.current, numbers);
    }
  }, [isComplete, numbers]);

  /* -------------------------------------------------
   * Helper – get canvas as Blob
   * ------------------------------------------------- */
  const getCanvasBlob = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (!canvasRef.current) {
        resolve(null);
        return;
      }
      canvasRef.current.toBlob(
        (blob) => resolve(blob),
        "image/png"
      );
    });
  }, []);

  /* -------------------------------------------------
   * Save image to local device
   * ------------------------------------------------- */
  const handleSaveImage = useCallback(async () => {
    if (!canvasRef.current || numbers.length < 6) return;
    setSaving(true);
    try {
      // Redraw to guarantee latest numbers
      drawResultCard(canvasRef.current, numbers);
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `pinball-lotto-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();

      setShowToast("\uC774\uBBF8\uC9C0 \uC800\uC7A5 \uC644\uB8CC! \uD83D\uDCF8");
      setTimeout(() => setShowToast(""), 2500);
    } catch (err) {
      console.error("Save failed:", err);
      setShowToast("\uC800\uC7A5 \uC2E4\uD328 \uD83D\uDE22");
      setTimeout(() => setShowToast(""), 2500);
    } finally {
      setSaving(false);
    }
  }, [numbers]);

  /* -------------------------------------------------
   * Kakao / General sharing
   * ------------------------------------------------- */
  const handleShareKakao = useCallback(async () => {
    if (numbers.length < 6) return;
    setSharing(true);

    const sorted = [...numbers].sort((a, b) => a - b);
    const text = `\uD83C\uDFB0 \uD540\uBCFC \uB85C\uB610 \uBC88\uD638!\n\uD83C\uDFC6 ${sorted.join(
      " - "
    )}\n\n\uB098\uB3C4 \uD540\uBCFC\uB85C \uB85C\uB610 \uBC88\uD638 \uBF51\uC544\uBCF4\uAE30 \u{1F449}`;
    const shareUrl = window.location.href;

    // Prefer Web Share API
    if (navigator.share) {
      try {
        const blob = await getCanvasBlob();
        const shareData: {
          title?: string;
          text?: string;
          url?: string;
          files?: File[];
        } = {
          title:
            "\uD540\uBCFC \uB85C\uB610 - \uC624\uB298\uC758 \uD589\uC6B4 \uBC88\uD638",
          text,
          url: shareUrl,
        };

        // If the platform can share files, attach the image
        if (blob) {
          const file = new File([blob], "pinball-lotto.png", {
            type: "image/png",
          });
          if (
            navigator.canShare &&
            navigator.canShare({ files: [file] })
          ) {
            shareData.files = [file];
          }
        }

        await navigator.share(shareData);
        setShowToast("\uACF5\uC720 \uC644\uB8CC! \uD83C\uDF89");
        setTimeout(() => setShowToast(""), 2500);
      } catch (err: any) {
        // If the user cancels or the API fails, fall back to clipboard copy
        if (err?.name !== "AbortError") {
          fallbackCopyShare(sorted, shareUrl);
        }
      }
    } else {
      // Desktop fallback – copy to clipboard
      fallbackCopyShare(sorted, shareUrl);
    }
    setSharing(false);
  }, [numbers, getCanvasBlob]);

  /* -------------------------------------------------
   * Fallback – copy share text (and URL) to clipboard
   * ------------------------------------------------- */
  const fallbackCopyShare = useCallback(
    async (sorted: number[], shareUrl: string) => {
      const text = `\uD83C\uDFB0 \uD540\uBCFC \uB85C\uB610 \uBC88\uD638!\n\uD83C\uDFC6 ${sorted.join(
        " - "
      )}\n\n\uB098\uB3C4 \uD540\uBCFC\uB85C \uB85C\uB610 \uBC88\uD638 \uBF51\uC544\uBCF4\uAE30 \u{1F449}\n${shareUrl}`;
      try {
        await navigator.clipboard.writeText(text);
        setShowToast(
          "\uACF5\uC720 \uD14D\uC2A4\uD2B8 \uBCF5\uC0AC \uC644\uB8CC! \uCE74\uD1A1\uC5D0 \uBD99\uC5EC\uB123\uAE30 \uD558\uC138\uC694 \uD83D\uDCCB"
        );
        setTimeout(() => setShowToast(""), 3000);
      } catch {
        setShowToast("\uBCF5\uC0AC \uC2E4\uD328 \uD83D\uDE22");
        setTimeout(() => setShowToast(""), 2500);
      }
    },
    []
  );

  if (!isComplete || numbers.length < 6) return null;

  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-5 border-2 border-amber-600/40 shadow-lg shadow-amber-900/20">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 flex items-center justify-center text-amber-400">
          <i className="ri-share-fill text-lg" />
        </div>
        <h3
          className="font-bold text-amber-400 text-sm tracking-wider uppercase"
          style={{ fontFamily: "Orbitron, sans-serif" }}
        >
          Share &amp; Save
        </h3>
      </div>

      {/* Preview card (hidden canvas for image generation) */}
      <div className="mb-4 rounded-xl overflow-hidden border border-amber-600/20">
        <canvas
          ref={canvasRef}
          className="w-full h-auto"
          style={{ display: "block" }}
        />
      </div>

      <div className="space-y-2.5">
        {/* 카카오톡 / 공유하기 */}
        <button
          type="button"
          onClick={handleShareKakao}
          disabled={sharing}
          className="w-full bg-yellow-400 hover:bg-yellow-300 disabled:bg-gray-700 disabled:text-gray-500 text-gray-900 font-bold py-3 px-5 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:cursor-not-allowed shadow-lg shadow-yellow-500/10 hover:shadow-yellow-400/20 text-sm"
          style={{ fontFamily: "Orbitron, sans-serif" }}
        >
          {sharing ? (
            <>
              <i className="ri-loader-4-line text-base animate-spin" />
              공유 중...
            </>
          ) : (
            <>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 3C6.48 3 2 6.58 2 10.94c0 2.8 1.86 5.27 4.66 6.67-.15.56-.96 3.6-.99 3.83 0 0-.02.17.09.24.11.06.24.01.24.01.32-.04 3.7-2.44 4.28-2.86.56.08 1.14.12 1.72.12 5.52 0 10-3.58 10-7.94C22 6.58 17.52 3 12 3z"
                  fill="#3C1E1E"
                />
              </svg>
              카카오톡 공유하기
            </>
          )}
        </button>

        {/* 이미지 저장 */}
        <button
          type="button"
          onClick={handleSaveImage}
          disabled={saving}
          className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 disabled:from-gray-700 disabled:to-gray-800 disabled:text-gray-500 text-white font-bold py-3 px-5 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:cursor-not-allowed shadow-lg shadow-emerald-500/20 hover:shadow-emerald-400/30 text-sm"
          style={{ fontFamily: "Orbitron, sans-serif" }}
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line text-base animate-spin" />
              저장 중...
            </>
          ) : (
            <>
              <i className="ri-image-fill text-base" />
              이미지 저장하기
            </>
          )}
        </button>
      </div>

      {/* Toast */}
      {showToast && (
        <div className="mt-3 py-2 px-4 bg-amber-500/20 border border-amber-500/40 rounded-xl text-center">
          <span className="text-amber-300 text-sm font-bold">{showToast}</span>
        </div>
      )}
    </div>
  );
}
